import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Users, Calendar, CheckCircle, Clock, Video, Mic, MessageCircle, Phone, Heart, AlertCircle, Activity } from 'lucide-react';

const now = new Date();
const currentHour = now.getHours();
const currentMinute = now.getMinutes();

const mockTodayAppointments = [
  {
    id: '1',
    time: '09:00 AM',
    patient: 'Mrs. Ngozi Okeke',
    type: 'Antenatal Check-up',
    mode: 'video',
    status: 'completed',
    scheduledTime: new Date(new Date().setHours(9, 0, 0, 0)),
  },
  {
    id: '2',
    time: `${currentHour}:${currentMinute.toString().padStart(2, '0')}`,
    patient: 'Mrs. Fatima Bello',
    type: 'Postnatal Follow-up',
    mode: 'video',
    status: 'confirmed',
    scheduledTime: new Date(new Date().setHours(currentHour, currentMinute, 0, 0)),
  },
  {
    id: '3',
    time: `${(currentHour + 1) % 24}:${currentMinute.toString().padStart(2, '0')}`,
    patient: 'Mrs. Adaobi Nwankwo',
    type: 'Pregnancy Consultation',
    mode: 'audio',
    status: 'confirmed',
    scheduledTime: new Date(new Date().setHours((currentHour + 1) % 24, currentMinute, 0, 0)),
  },
  {
    id: '4',
    time: `${(currentHour + 2) % 24}:${currentMinute.toString().padStart(2, '0')}`,
    patient: 'Mrs. Aisha Mohammed',
    type: 'Child Health Review',
    mode: 'chat',
    status: 'confirmed',
    scheduledTime: new Date(new Date().setHours((currentHour + 2) % 24, currentMinute, 0, 0)),
  },
];

const DoctorDashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const canJoinConsultation = (appointment) => {
    const now = new Date();
    const scheduledTime = new Date(appointment.scheduledTime);
    const fifteenMinutesBefore = new Date(scheduledTime.getTime() - 15 * 60 * 1000);
    const thirtyMinutesAfter = new Date(scheduledTime.getTime() + 30 * 60 * 1000);
    
    return now >= fifteenMinutesBefore && now <= thirtyMinutesAfter;
  };

  const handleJoinConsultation = (appointment) => {
    navigate(`/doctor/consultation/${appointment.id}`, {
      state: {
        appointment: {
          ...appointment,
          doctor: 'You',
          specialty: 'OB/GYN Specialist',
        },
      },
    });
  };
  const stats = {
    totalPatients: 127,
    todayAppointments: mockTodayAppointments.length,
    completedToday: mockTodayAppointments.filter(apt => apt.status === 'completed').length,
    upcomingToday: mockTodayAppointments.filter(apt => apt.status === 'upcoming').length,
  };
  
  // Translation helpers
  const translateAppointmentType = (type) => {
    const typeMap = {
      'Antenatal Check-up': 'antenatalCheckup',
      'Postnatal Follow-up': 'postnatalFollowup',
      'Pregnancy Consultation': 'pregnancyConsultation',
      'Child Health Review': 'childHealthReview',
    };
    return t(typeMap[type] || type);
  };
  
  const translateMode = (mode) => {
    return t(mode);
  };
  
  const translateStatus = (status) => {
    return t(status);
  };

  const getModeIcon = (mode) => {
    const iconClass = "w-5 h-5";
    switch (mode) {
      case 'video': return <Video className={iconClass} />;
      case 'audio': return <Mic className={iconClass} />;
      case 'chat': return <MessageCircle className={iconClass} />;
      default: return <Phone className={iconClass} />;
    }
  };

  return (
    <div className="p-6 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                {t('doctorDashboard')}
              </h1>
              <p className="text-gray-600 text-sm">{t('welcomeBackDoctor')}</p>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('totalPatients')}</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.totalPatients}</h3>
              </div>
              <div className="p-2 bg-emerald-500 rounded-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('todayAppointments')}</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.todayAppointments}</h3>
              </div>
              <div className="p-2 bg-emerald-500 rounded-lg">
                <Calendar className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('completedToday')}</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.completedToday}</h3>
              </div>
              <div className="p-2 bg-emerald-500 rounded-lg">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">{t('upcomingToday')}</p>
                <h3 className="text-2xl font-bold text-gray-900">{stats.upcomingToday}</h3>
              </div>
              <div className="p-2 bg-amber-500 rounded-lg">
                <Clock className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Today's Schedule */}
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{t('todaysAppointments')}</h2>
              <div className="space-y-3">
                {mockTodayAppointments.map(appointment => (
                  <div
                    key={appointment.id}
                    className={`p-3 rounded-lg border transition-all ${
                      appointment.status === 'completed'
                        ? 'bg-emerald-50 border-emerald-200'
                        : 'bg-white border-gray-200 hover:border-emerald-200'
                    }`}
                  >
                    <div className="flex items-start justify-between flex-wrap gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="text-center min-w-[60px] flex-shrink-0">
                          <div className={`text-sm font-bold ${appointment.status === 'completed' ? 'text-emerald-600' : 'text-emerald-600'}`}>
                            {appointment.time}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">{appointment.patient}</h3>
                          <p className="text-xs text-gray-600 truncate mt-0.5">{translateAppointmentType(appointment.type)}</p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <div className="flex items-center gap-1.5 text-gray-600">
                              <div className={`p-1 rounded-md ${
                                appointment.mode === 'video' ? 'bg-emerald-100' : 
                                appointment.mode === 'audio' ? 'bg-emerald-100' : 'bg-emerald-100'
                              }`}>
                                {getModeIcon(appointment.mode)}
                              </div>
                              <span className="text-xs font-medium">
                                {translateMode(appointment.mode)}
                              </span>
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              appointment.status === 'completed'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              {translateStatus(appointment.status)}
                            </span>
                          </div>
                        </div>
                      </div>
                      {appointment.status === 'upcoming' && (
                        <div className="flex-shrink-0">
                          {canJoinConsultation(appointment) ? (
                            <button 
                              onClick={() => handleJoinConsultation(appointment)}
                              className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white text-sm rounded-lg hover:from-emerald-700 hover:to-emerald-600 transition-all font-medium animate-pulse"
                            >
                              {t('joinCall')}
                            </button>
                          ) : (
                            <button 
                              disabled
                              className="px-4 py-2 bg-gray-300 text-gray-500 text-sm rounded-lg font-medium cursor-not-allowed"
                            >
                              {t('joinCall')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">{t('thisWeek')}</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Calendar className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{t('appointments')}</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">32</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Users className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{t('newPatients')}</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">8</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-pink-100 rounded-lg">
                      <Heart className="w-5 h-5 text-pink-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{t('followUps')}</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">12</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-6">{t('recentAlerts')}</h3>
              <div className="space-y-3">
                <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-500 rounded-lg shadow-md flex-shrink-0">
                      <AlertCircle className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-sm text-amber-900">
                      <strong className="font-semibold">High BP:</strong> Mrs. Ngozi - requires follow-up
                    </p>
                  </div>
                </div>
                <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-200">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-emerald-500 rounded-lg shadow-md flex-shrink-0">
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-sm text-emerald-900">
                      <strong className="font-semibold">Reminder:</strong> 2 pending prescriptions
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-2xl shadow-lg shadow-emerald-500/30 text-white p-6">
              <h3 className="text-lg font-bold mb-4">{t('quickActions')}</h3>
              <div className="space-y-3">
                <Link to="/doctor/appointments" className="group w-full bg-white/20 hover:bg-white/30 py-3 rounded-xl transition-all flex items-center justify-center gap-2 font-medium backdrop-blur-sm">
                  <Calendar className="w-5 h-5" />
                  {t('appointments')}
                </Link>
                <Link to="/doctor/patients" className="group w-full bg-white/20 hover:bg-white/30 py-3 rounded-xl transition-all flex items-center justify-center gap-2 font-medium backdrop-blur-sm">
                  <Users className="w-5 h-5" />
                  {t('viewAllPatients')}
                </Link>
                <Link to="/doctor/health-tracking" className="group w-full bg-white/20 hover:bg-white/30 py-3 rounded-xl transition-all flex items-center justify-center gap-2 font-medium backdrop-blur-sm">
                  <Heart className="w-5 h-5" />
                  {t('healthTrackingPage')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorDashboard;
